// ============================================
// APPLICATION - Clientes Hooks
// ============================================

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clienteRepository } from '../../infrastructure/http/cliente.repository';
import type {
  BuscarClienteDto,
  ActualizarClienteFiadoDto,
  CrearClienteFiadoDto
} from '../../domain/dtos';
import { handleApiError } from '../../infrastructure/http/axios-client';
import { queryKeys } from '../query-keys';

/**
 * Hook para buscar clientes fiados por nombre, teléfono o RUT+DV
 */
export const useBuscarClientesFiados = () => {
  return useMutation({
    mutationFn: (dto: BuscarClienteDto) => clienteRepository.findClientesFiados(dto),
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para actualizar un cliente fiado
 */
export const useActualizarClienteFiado = () => {
  return useMutation({
    mutationFn: (dto: ActualizarClienteFiadoDto) => clienteRepository.updateClienteFiado(dto),
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para crear un cliente fiado
 */
export const useCrearClienteFiado = () => {
  return useMutation({
    mutationFn: (dto: CrearClienteFiadoDto) => clienteRepository.createClienteFiado(dto),
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para guardar o actualizar el alias de un cliente en el almacén
 */
export const useSetNombreClienteAlmacen = (clienteAlmacenId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (nombre: string) => clienteRepository.setNombreClienteAlmacen(clienteAlmacenId, nombre),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fiados.pendientes });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.fiados.pendientes, 'detalle', clienteAlmacenId] });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para eliminar el alias de un cliente en el almacén
 */
export const useDeleteNombreClienteAlmacen = (clienteAlmacenId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clienteRepository.deleteNombreClienteAlmacen(clienteAlmacenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fiados.pendientes });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.fiados.pendientes, 'detalle', clienteAlmacenId] });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};
